(() => {
  const CLOSE_DELAY_MS = 100;
  const CUSTOMER_UPGRADE_URL = '/v2/#/customer-upgrade';
  let nextGateId = 0;

  angular
    .module('kareo.ui.shared.directives.trial-feature-gate', [])
    .directive('trialFeatureGate', trialFeatureGate);

  trialFeatureGate.$inject = [
    '$compile',
    '$document',
    '$parse',
    '$timeout',
    '$window',
  ];

  function trialFeatureGate($compile, $document, $parse, $timeout, $window) {
    return {
      restrict: 'A',
      link,
    };

    function link(scope, element, attrs) {
      const action = $parse(attrs.trialAction);
      const showUpgradeCta = $parse(attrs.trialShowUpgradeCta || 'false');
      const upgradeUrl = attrs.trialUpgradeUrl
        ? $parse(attrs.trialUpgradeUrl)
        : () => CUSTOMER_UPGRADE_URL;
      const gateId = nextGateId++;
      const gateScope = scope.$new();
      const wrapper = element.parent();
      const menuItem =
        attrs.trialGatePlacement === 'right-start' &&
        element[0].closest &&
        element[0].closest('li');
      const overflowContainer =
        attrs.trialGatePlacement === 'right-start' &&
        element[0].closest &&
        element[0].closest('.nav-list-submenu');
      let closeTimer;
      let cardPositionTimer;
      let suppressNextTriggerFocus = false;
      let firstCardActionFocusTimer;

      gateScope.trialGate = {
        cardDescriptionId: `trial-feature-gate-description-${gateId}`,
        cardId: `trial-feature-gate-card-${gateId}`,
        cardTitleId: `trial-feature-gate-title-${gateId}`,
        isCardOpen: false,
        isTrial: false,
        showUpgradeCta: false,
        triggerDescriptionId: `trial-feature-gate-trigger-description-${gateId}`,
        upgradeUrl: CUSTOMER_UPGRADE_URL,
      };
      gateScope.cancelScheduledClose = cancelScheduledClose;
      gateScope.scheduleClose = scheduleClose;

      wrapper.addClass('trial-feature-gate');
      if (attrs.trialGatePlacement === 'right-start') {
        wrapper.addClass('trial-feature-gate--right-start');
      }
      element.addClass('trial-feature-gate__trigger');

      if (element[0].tagName === 'A' && !attrs.href) {
        element.attr('role', 'button');
        element.attr('tabindex', '0');
      }

      const lockElement = $compile(
        require('./trial-feature-gate-lock.pug')(),
      )(gateScope);
      element[0].insertBefore(lockElement[0], element[0].firstChild);
      const cardElement = $compile(
        require('./trial-feature-gate-card.pug')(),
      )(gateScope);
      wrapper.append(cardElement);

      const stopWatchingStatus = scope.$watch(
        attrs.trialStatus,
        updateTrialStatus,
      );
      const stopWatchingUpgradeCta = scope.$watch(
        () => showUpgradeCta(scope),
        updateShowUpgradeCta,
      );
      const stopWatchingUpgradeUrl = scope.$watch(
        () => upgradeUrl(scope),
        updateUpgradeUrl,
      );

      element.on('click', activate);
      wrapper.on('mouseenter', handleMouseEnter);
      wrapper.on('mouseleave', scheduleClose);
      wrapper.on('focusin', handleFocusIn);
      wrapper.on('focusout', scheduleClose);
      wrapper.on('keydown', handleKeyDown);
      cardElement.on('click', stopCardClick);
      cardElement.on('mouseenter', cancelScheduledClose);
      cardElement.on('mouseleave', scheduleClose);
      $document.on('click', handleDocumentClick);
      $window.addEventListener('resize', scheduleCardPosition);
      $window.addEventListener('scroll', scheduleCardPosition, true);
      scope.$on('$destroy', destroy);

      function activate(event) {
        if (
          gateScope.trialGate.isTrial ||
          gateScope.trialGate.isTrialStatusPending
        ) {
          event.preventDefault();
          event.stopPropagation();
          openCard();
          return;
        }

        apply(() => action(scope, { $event: event }));
      }

      function apply(callback) {
        if (scope.$root.$$phase) {
          callback();
          return;
        }

        scope.$apply(callback);
      }

      function cancelScheduledClose() {
        if (closeTimer) {
          $timeout.cancel(closeTimer);
          closeTimer = null;
        }
      }

      function cancelScheduledCardPosition() {
        if (cardPositionTimer) {
          $timeout.cancel(cardPositionTimer);
          cardPositionTimer = null;
        }
      }

      function cancelScheduledFirstCardActionFocus() {
        if (firstCardActionFocusTimer) {
          $timeout.cancel(firstCardActionFocusTimer);
          firstCardActionFocusTimer = null;
        }
      }

      function closeCard(restoreFocus) {
        cancelScheduledClose();
        cancelScheduledFirstCardActionFocus();
        if (!gateScope.trialGate.isCardOpen) {
          return;
        }

        setOverflowVisible(false);
        wrapper.removeClass('trial-feature-gate--left-start');
        apply(() => {
          gateScope.trialGate.isCardOpen = false;
        });
        element.attr('aria-expanded', 'false');

        if (restoreFocus) {
          suppressNextTriggerFocus = true;
          element[0].focus();
        }
      }

      function destroy() {
        cancelScheduledClose();
        cancelScheduledCardPosition();
        cancelScheduledFirstCardActionFocus();
        setOverflowVisible(false);
        stopWatchingStatus();
        stopWatchingUpgradeCta();
        stopWatchingUpgradeUrl();
        element.off('click', activate);
        wrapper.off('mouseenter', handleMouseEnter);
        wrapper.off('mouseleave', scheduleClose);
        wrapper.off('focusin', handleFocusIn);
        wrapper.off('focusout', scheduleClose);
        wrapper.off('keydown', handleKeyDown);
        cardElement.off('click', stopCardClick);
        cardElement.off('mouseenter', cancelScheduledClose);
        cardElement.off('mouseleave', scheduleClose);
        $document.off('click', handleDocumentClick);
        $window.removeEventListener('resize', scheduleCardPosition);
        $window.removeEventListener('scroll', scheduleCardPosition, true);
        gateScope.$destroy();
      }

      function handleDocumentClick(event) {
        const card = $document[0].getElementById(
          gateScope.trialGate.cardId,
        );
        const cardTarget = event.target?.closest?.(
          '.trial-feature-gate__card',
        );
        if (
          gateScope.trialGate.isCardOpen &&
          !wrapper[0].contains(event.target) &&
          !card?.contains(event.target) &&
          !cardTarget
        ) {
          closeCard(false);
        }
      }

      function stopCardClick(event) {
        cancelScheduledClose();
        event.stopImmediatePropagation?.();
        event.stopPropagation();
      }

      function handleFocusIn(event) {
        if (
          suppressNextTriggerFocus &&
          event.target === element[0]
        ) {
          suppressNextTriggerFocus = false;
          return;
        }

        openCard();
      }

      function handleMouseEnter() {
        openCard();
      }

      function handleKeyDown(event) {
        if (event.key === 'Escape' || event.which === 27) {
          if (gateScope.trialGate.isCardOpen) {
            event.preventDefault();
            event.stopPropagation();
            closeCard(true);
          }
          return;
        }

        const isActivationKey =
          event.key === 'Enter' ||
          event.key === ' ' ||
          event.which === 13 ||
          event.which === 32;
        const isTrigger = event.target === element[0];

        if (
          isTrigger &&
          gateScope.trialGate.isTrial &&
          gateScope.trialGate.isCardOpen &&
          event.key === 'Tab' &&
          !event.shiftKey
        ) {
          event.preventDefault();
          event.stopPropagation();
          scheduleFirstCardActionFocus();
          return;
        }

        if (
          isTrigger &&
          gateScope.trialGate.isTrial &&
          isActivationKey
        ) {
          event.preventDefault();
          event.stopPropagation();
          openCard(true);
          return;
        }

        if (
          isTrigger &&
          isActivationKey &&
          (element[0].tagName === 'A' ||
            gateScope.trialGate.isTrial ||
            gateScope.trialGate.isTrialStatusPending)
        ) {
          activate(event);
        }
      }

      function openCard(shouldFocusFirstAction = false) {
        if (!gateScope.trialGate.isTrial) {
          return;
        }

        cancelScheduledClose();
        setOverflowVisible(true);
        apply(() => {
          gateScope.trialGate.isCardOpen = true;
        });
        element.attr('aria-expanded', 'true');
        scheduleCardPosition();

        if (shouldFocusFirstAction) {
          scheduleFirstCardActionFocus();
        }
      }

      function positionCard() {
        cardPositionTimer = null;

        if (
          !gateScope.trialGate.isCardOpen ||
          attrs.trialGatePlacement !== 'right-start'
        ) {
          return;
        }

        const card = wrapper[0].querySelector('.trial-feature-gate__card');
        if (!card) {
          return;
        }

        const cardRect = card.getBoundingClientRect();
        const triggerRect = element[0].getBoundingClientRect();
        const wrapperRect = wrapper[0].getBoundingClientRect();
        const menuItemRect = menuItem?.getBoundingClientRect() || triggerRect;
        const viewportWidth = $window.innerWidth;
        const cardWidth = cardRect.width || 400;
        const canPlaceRight =
          menuItemRect.right + 12 + cardWidth <= viewportWidth;
        const canPlaceLeft = menuItemRect.left >= cardWidth + 12;
        const shouldPlaceLeft = !canPlaceRight && canPlaceLeft;
        const arrowTop = Math.max(
          12,
          Math.min(triggerRect.height / 2 - 7, (cardRect.height || 400) - 26),
        );

        wrapper.toggleClass('trial-feature-gate--left-start', shouldPlaceLeft);
        wrapper[0].style.setProperty(
          '--trial-feature-gate-card-left',
          `${menuItemRect.right - wrapperRect.left + 12}px`,
        );
        wrapper[0].style.setProperty(
          '--trial-feature-gate-arrow-top',
          `${arrowTop}px`,
        );
      }

      function scheduleCardPosition() {
        cancelScheduledCardPosition();
        cardPositionTimer = $timeout(positionCard, 0, false);
      }

      function scheduleFirstCardActionFocus() {
        cancelScheduledFirstCardActionFocus();
        firstCardActionFocusTimer = $timeout(() => {
          firstCardActionFocusTimer = null;
          const firstCardAction = wrapper[0].querySelector(
            '.trial-feature-gate__upgrade, .trial-feature-gate__help',
          );
          firstCardAction?.focus();
        }, 0, false);
      }

      function scheduleClose() {
        cancelScheduledClose();
        if (!gateScope.trialGate.isCardOpen) {
          return;
        }

        closeTimer = $timeout(() => {
          closeCard(false);
        }, CLOSE_DELAY_MS);
      }

      function setOverflowVisible(isVisible) {
        if (!overflowContainer) {
          return;
        }

        overflowContainer.classList.toggle(
          'trial-feature-gate-overflow-visible',
          isVisible,
        );
      }

      function updateTrialStatus(trialStatus) {
        gateScope.trialGate.isTrial = trialStatus === true;
        gateScope.trialGate.isTrialStatusPending = trialStatus == null;

        if (gateScope.trialGate.isTrialStatusPending) {
          element.attr('aria-disabled', 'true');
        } else {
          element.removeAttr('aria-disabled');
        }

        if (gateScope.trialGate.isTrial) {
          element.attr(
            'aria-describedby',
            gateScope.trialGate.triggerDescriptionId,
          );
          element.attr('aria-controls', gateScope.trialGate.cardId);
          element.attr('aria-expanded', 'false');
          element.attr('aria-haspopup', 'dialog');
          return;
        }

        element.removeAttr('aria-controls');
        element.removeAttr('aria-describedby');
        element.removeAttr('aria-expanded');
        element.removeAttr('aria-haspopup');
        gateScope.trialGate.isCardOpen = false;
        cancelScheduledCardPosition();
        cancelScheduledFirstCardActionFocus();
        wrapper.removeClass('trial-feature-gate--left-start');
        setOverflowVisible(false);
      }

      function updateShowUpgradeCta(value) {
        gateScope.trialGate.showUpgradeCta = value === true;
      }

      function updateUpgradeUrl(value) {
        gateScope.trialGate.upgradeUrl =
          typeof value === 'string' && value.length > 0
            ? value
            : CUSTOMER_UPGRADE_URL;
      }
    }
  }
})();
